import React from 'react';
import PropTypes from 'prop-types';
import AddRelatedButton from 'pages/products/productRelated/buttons/AddRelatedButton';
import Card from 'components/ui/card/Card';
import CardText from 'components/ui/card/CardText';
import FieldTitle from 'components/aor/FieldTitle';
import Loader from 'components/loading/Loader';
import Pagination from 'components/list/Pagination/Pagination';
import HeaderColumn from 'components/ui/table/HeaderColumn';
import Table from 'components/ui/table/Table';
import TableRow from 'components/ui/table/TableRow';
import TextField from 'components/show/TextField';
import TableBodyCell from 'components/table/TableBodyCell';
import SubCategoryField from './SubCategoryField';
import compose from 'recompose/compose';
import styled from 'styled-components';
import translate from 'components/hoc/translate';
import utils from 'helpers/utils';
import { SORT_ASC, SORT_DESC } from 'store/actions/types';
import { connect } from 'react-redux';
import { fetchProducts } from 'store/actions/products/fetchProducts';

const ListStylingWrapper = styled.div`
    position: relative;
`;

const LoaderStylingWrapper = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: #FFF;
    opacity: .9;
    z-index: 999;
    pointer-events: none;
`;

const LoaderStylingBlock = styled.div`
    margin-top: 50px;
    display: flex;
    width: 150px;
    height: 150px;
    justify-content: center;
    align-items: center;
    opacity: 1;
`;

const StyledCard = styled(Card)`
    > div {
        padding-bottom: 0 !important;
    }
`;

const StyledDeleteIcon = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
`;

class ProductTable extends React.Component {
    componentDidMount() {
        this.fetchData();
    }

    componentDidUpdate(prevProps) {
        const { filterList } = this.props;

        const diff = utils.diffObjects(prevProps.filterList, filterList);

        if (!utils.isEmpty(diff)) {
            this.fetchData();
        }
    }

    fetchData() {
        const { dispatchAction, filterList } = this.props;

        dispatchAction(filterList);
    }

    setPage = (newPage) => {
        const { updateListParams } = this.props;

        if (!newPage) {
            return;
        }

        if (this.props.page === newPage) {
            return;
        }

        const params = {
            page: newPage,
        };

        updateListParams({
            type: 'PRODUCTS_LIST_PAGE',
            payload: params,
        });
    }

    setSort = (label) => {
        if (!label) {
            return;
        }

        const { params: { order }, updateListParams } = this.props;

        const newOrder = (order === SORT_DESC) ? SORT_ASC : SORT_DESC;

        const params = {
            field: label,
            order: newOrder,
        };

        updateListParams({
            type: 'PRODUCTS_LIST_SORT',
            payload: params,
        });
    }

    render() {
        const {
            products,
            handleAdd,
            loading,
            offset,
            page,
            productId,
            productRelated,
            size,
            total,
            translate,
        } = this.props;

        if (loading) {
            return (
                <LoaderStylingWrapper>
                    <LoaderStylingBlock>
                        <Loader />
                    </LoaderStylingBlock>
                </LoaderStylingWrapper>
            );
        }

        if (total === 0) {
            return (
                <ListStylingWrapper>
                    <StyledCard>
                        <CardText style={ { padding: '20px' } }>
                            { translate('aor.navigation.no_results') }
                        </CardText>
                    </StyledCard>
                </ListStylingWrapper>
            );
        }

        return (
            <ListStylingWrapper>
                <StyledCard>
                    <Table responsive='scroll'>
                        <TableRow>
                            <HeaderColumn style={ { width: '50%' } }>
                                <FieldTitle
                                    label='resources.products.fields.title'
                                    resource='products'
                                    source='productRelated'
                                />
                            </HeaderColumn>
                            <HeaderColumn style={ { width: '20%' } }>
                                <FieldTitle
                                    label='resources.products.fields.category_id'
                                    resource='products'
                                    source='category_id'
                                />
                            </HeaderColumn>
                            <HeaderColumn style={ { width: '20%' } }>
                                <FieldTitle
                                    label='resources.products.fields.subcategory_id'
                                    resource='products'
                                    source='subcategory_id'
                                />
                            </HeaderColumn>
                            <HeaderColumn style={ { width: '10%' } }></HeaderColumn>
                        </TableRow>

                        { utils.map(products, function (product) {
                            // Don't show the same Product.
                            if (product.id === productId) {
                                return;
                            }

                            // Don't show related products.
                            const related = !!productRelated[product.id];

                            if (related) {
                                return;
                            }

                            return (
                                <TableRow
                                    key={ product.id }
                                >
                                    <TableBodyCell
                                        field={ <TextField value={ product.title } /> }
                                        record={ product }
                                        source='title'
                                    />
                                    <TableBodyCell
                                        field={ <TextField value={ product.category.name } /> }
                                        record={ product }
                                        source='title'
                                    />
                                    <TableBodyCell
                                        field={
                                            <SubCategoryField
                                                id={ product.subcategory_id }
                                                parentId={ product.category_id }
                                            />
                                        }
                                        record={ product }
                                        source='title'
                                    />
                                    <TableBodyCell
                                        field={
                                            <StyledDeleteIcon>
                                                <AddRelatedButton
                                                    handleAdd={ () => {
                                                        return handleAdd(product.id);
                                                    } }
                                                />
                                            </StyledDeleteIcon>
                                        }
                                        record={ product }
                                        resource='products'
                                    />
                                </TableRow>
                            );
                        }) }
                    </Table>

                    <Pagination
                        currentPage={ page }
                        offset={ offset }
                        setPage={ this.setPage }
                        size={ size }
                        total={ total }
                    />
                </StyledCard>
            </ListStylingWrapper>
        );
    }
}

ProductTable.propTypes = {
    dispatchAction: PropTypes.func.isRequired,
    filterList: PropTypes.object.isRequired,
    handleAdd: PropTypes.func.isRequired,
    loading: PropTypes.bool.isRequired,
    offset: PropTypes.number,
    page: PropTypes.number,
    params: PropTypes.object,
    productId: PropTypes.string.isRequired,
    productRelated: PropTypes.object,
    products: PropTypes.object,
    size: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
    ]),
    sort: PropTypes.shape({
        field: PropTypes.string.isRequired,
        order: PropTypes.string,
    }).isRequired,
    total: PropTypes.number,
    translate: PropTypes.func.isRequired,
    updateListParams: PropTypes.func.isRequired,
};

const selectFilterData = (state) => {
    return {
        page: state.page,
        size: state.size,
        field: state.field,
        order: state.order,
        filter: state.filter,
    };
};

const getProducts = (state) => {
    const { data, ids } = state;

    let list = {};

    utils.each(ids, (id) => {
        if (!data.hasOwnProperty(id)) {
            return;
        }

        list[id] = data[id];
    });

    return list;
};

const mapStateToProps = (state, props) => {
    const { productId } = props;

    const stateResource = state.products;

    return {
        filterCategory: stateResource.filterCategory,
        loading: stateResource.loading,
        offset: stateResource.offset,
        page: stateResource.page,
        filterList: selectFilterData(stateResource),
        params: {
            page: stateResource.page,
            field: stateResource.field,
            filter: stateResource.filter,
            order: stateResource.order,
            size: stateResource.size,
        },
        productRelated: state.productRelated.data[productId],
        products: getProducts(stateResource),
        size: stateResource.size,
        sort: {
            field: stateResource.field,
            order: stateResource.order,
        },
        total: stateResource.total,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        dispatchAction: (values) => {
            dispatch(fetchProducts(values));
        },
        updateListParams: (action) => {
            dispatch(action);
        },
    };
};

const enhance = compose(
    connect(
        mapStateToProps,
        mapDispatchToProps,
    ),
    translate,
);

export default enhance(ProductTable);



// WEBPACK FOOTER //
// ./src/pages/products/productRelated/ProductTable.js